import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import api from '../../services/apiService';

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const isValid = password.length >= 8 && password === confirmPassword;
  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) return;
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/set-password', { token, password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-surface">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface-secondary px-6 py-12 border border-skin sm:rounded-lg sm:px-12 text-center">
            <p className="text-sm/6 text-skin-secondary">Lien d'invitation invalide.</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 text-sm/6 font-medium text-primary-600 hover:text-primary-500"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-surface">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-surface-secondary px-6 py-12 border border-skin sm:rounded-lg sm:px-12 text-center"
          >
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500" />
            <h2 className="mt-4 text-xl font-semibold text-skin-primary">Mot de passe défini</h2>
            <p className="mt-2 text-sm/6 text-skin-secondary">Redirection vers la connexion…</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-surface">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="flex justify-center">
          <img src="/brand/m-bao-logo.png" alt="m-bao.fr" width={542} height={320} className="h-auto w-48 sm:w-64 dark:hidden" />
          <img src="/brand/m-bao-logo-on-dark.png" alt="m-bao.fr" width={542} height={320} className="hidden h-auto w-48 sm:w-64 dark:block" />
        </h1>
        <p className="mx-auto mt-2 max-w-68 text-center text-xs font-medium uppercase tracking-[0.2em] text-balance text-primary-900">
          Les bonnes relations font les bonnes affaires.
        </p>
        <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-skin-primary">
          Créez votre mot de passe
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-surface-secondary px-6 py-12 border border-skin sm:rounded-lg sm:px-12"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm/6 text-red-700"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm/6 font-medium text-skin-primary">
                Mot de passe
              </label>
              <div className="relative mt-2">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md bg-surface-secondary px-3 py-1.5 pr-10 text-base text-skin-primary border-0 ring-1 ring-skin-strong ring-inset placeholder:text-skin-tertiary focus:ring-2 focus:ring-primary-600 focus:ring-inset sm:text-sm/6"
                  placeholder="8 caractères minimum"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-skin-tertiary hover:text-skin-secondary"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {password.length > 0 && password.length < 8 && (
                <p className="mt-2 text-xs text-amber-600">
                  {password.length}/8 caractères
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirm" className="block text-sm/6 font-medium text-skin-primary">
                Confirmer le mot de passe
              </label>
              <div className="mt-2">
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={`block w-full rounded-md bg-surface-secondary px-3 py-1.5 text-base text-skin-primary border-0 ring-1 ring-inset placeholder:text-skin-tertiary sm:text-sm/6 ${
                    mismatch
                      ? 'ring-red-300 focus:ring-2 focus:ring-red-500 focus:ring-inset'
                      : 'ring-skin-strong focus:ring-2 focus:ring-primary-600 focus:ring-inset'
                  }`}
                  placeholder="Retapez votre mot de passe"
                  required
                />
              </div>
              {mismatch && (
                <p className="mt-2 text-xs text-red-600">
                  Les mots de passe ne correspondent pas.
                </p>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={!isValid || loading}
                className="flex w-full justify-center items-center rounded-md bg-primary-600 px-3 py-1.5 text-sm/6 font-semibold text-white hover:bg-primary-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <svg className="h-5 w-5 animate-spin text-white" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  'Définir mon mot de passe'
                )}
              </button>
            </div>
          </form>
        </motion.div>

        <p className="mt-6 text-center text-xs text-skin-tertiary">
          Plateforme réservée aux membres invités
        </p>
      </div>
    </div>
  );
}
